import React, { useEffect, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import type { Swiper as SwiperClass, Swiper as SwiperType } from "swiper";

import { RoomMovie } from "../../../api/room";
import RoomMovieCard from "./RoomMovieCard";

import "swiper/css";
import "swiper/css/pagination";

import styles from "./RoomMovieCards.module.scss";
import useInterfaceContext from "../../../components/providers/interface";

interface Props {
  movies: RoomMovie[];
  activeIndex: number;
  onMovieClick: (index: number) => void;
  onSwiper?: (swiper: SwiperType) => void;
  onSlideChange?: (index: number) => void;
  syncedHeight?: number | null;
  votedIds?: Set<number>;
}

const POSTER_RATIO = 1 / 2;

const RoomMovieCards: React.FC<Props> = ({
                                           movies,
                                           activeIndex,
                                           onMovieClick,
                                           onSwiper,
                                           onSlideChange,
                                           syncedHeight,
                                           votedIds,
                                         }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const swiperInstanceRef = useRef<SwiperType | null>(null);
  const initialized = useRef(false);

  const { isMobile } = useInterfaceContext();

  const thumbWidth =
    !isMobile && syncedHeight ? syncedHeight * POSTER_RATIO : null;

  const updateOffsetAfter = () => {
    const swiper = swiperInstanceRef.current;
    const containerEl = containerRef.current;
    if (!swiper || !containerEl) return;

    if (isMobile) {
      if (swiper.params.slidesOffsetAfter !== 0) {
        swiper.params.slidesOffsetAfter = 0;
        swiper.update();
      }
      return;
    }

    const containerWidth = containerEl.offsetWidth;
    const firstSlideEl = swiper.slides?.[0] as HTMLElement | undefined;
    const slideWidth = firstSlideEl ? firstSlideEl.offsetWidth : 0;

    const offsetAfter = Math.max(containerWidth - slideWidth, 0);

    swiper.params.slidesOffsetAfter = offsetAfter;
    swiper.update();
  };

  useEffect(() => {
    updateOffsetAfter();
    window.addEventListener("resize", updateOffsetAfter);
    return () => window.removeEventListener("resize", updateOffsetAfter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movies.length, thumbWidth]);

  if (!movies.length) return null;

  return (
    <div
      className={styles.cardsContainer}
      ref={containerRef}
      style={
        !isMobile && syncedHeight
          ? ({
            "--synced-height": `${syncedHeight}px`,
            "--thumb-width": `${thumbWidth}px`,
          } as React.CSSProperties)
          : undefined
      }
    >
      <Swiper
        modules={isMobile ? [Pagination] : []}
        onSwiper={(swiper: SwiperType) => {
          swiperInstanceRef.current = swiper;
          onSwiper?.(swiper);
          requestAnimationFrame(updateOffsetAfter);
        }}
        spaceBetween={16}
        slidesPerView="auto"
        slidesPerGroup={1}
        slidesPerGroupAuto={false}
        slidesOffsetBefore={0}
        breakpoints={{
          0: {
            slidesPerView: 1,
            spaceBetween: 0,
          },
          1024: {
            slidesPerView: "auto",
            spaceBetween: 16,
          },
        }}
        pagination={
          isMobile
            ? {
              clickable: true,
              dynamicBullets: true,
              dynamicMainBullets: 5,
            }
            : false
        }
        onSlideChange={(swiper: SwiperClass) => {
          if (isMobile && !initialized.current) {
            initialized.current = true;
            swiper.pagination?.update();
          }
          onSlideChange?.(swiper.activeIndex);
        }}
        className={styles.swiper}
      >
        {movies.map((movie, index) => (
          <SwiperSlide
            key={movie.id}
            className={`${styles.slide} ${activeIndex === index ? styles.active : ""}`}
          >
            <RoomMovieCard
              movie={movie}
              onClick={() => onMovieClick(index)}
              isActive={activeIndex === index}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export default RoomMovieCards;
